<script setup>
import { ref, computed, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Sélectionner une date',
  },
});

const emit = defineEmits(['update:modelValue']);

const showCalendar = ref(false);
const selectedDate = ref(props.modelValue || '');

// Mettre à jour quand le modelValue change
watch(
  () => props.modelValue,
  newValue => {
    selectedDate.value = newValue || '';
  }
);

// Date actuelle pour le calendrier
const currentMonth = ref(new Date());

// Formater la date affichée
const formattedDate = computed(() => {
  if (!selectedDate.value) return '';
  const date = new Date(selectedDate.value);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
});

// Jours de la semaine
const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Nom du mois et année
const monthYearLabel = computed(() => {
  return currentMonth.value.toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
});

// Générer les jours du calendrier
const calendarDays = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();

  // Premier jour du mois (0 = Dimanche, 1 = Lundi, etc.)
  const firstDay = new Date(year, month, 1).getDay();
  // Dernier jour du mois
  const lastDate = new Date(year, month + 1, 0).getDate();

  const days = [];

  // Ajuster pour commencer le lundi (1) au lieu de dimanche (0)
  const startPadding = firstDay === 0 ? 6 : firstDay - 1;

  // Ajouter les jours vides au début
  for (let i = 0; i < startPadding; i += 1) {
    days.push({ date: null, isCurrentMonth: false });
  }

  // Ajouter les jours du mois
  for (let date = 1; date <= lastDate; date += 1) {
    days.push({
      date,
      isCurrentMonth: true,
      fullDate: new Date(year, month, date),
    });
  }

  return days;
});

// Vérifier si une date est aujourd'hui
const isToday = date => {
  const today = new Date();
  return (
    date &&
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Vérifier si une date est sélectionnée
const isSelected = date => {
  if (!selectedDate.value || !date) return false;
  const selected = new Date(selectedDate.value);
  return (
    date.getDate() === selected.getDate() &&
    date.getMonth() === selected.getMonth() &&
    date.getFullYear() === selected.getFullYear()
  );
};

// Sélectionner une date
const selectDate = date => {
  if (!date) return;

  // Format YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  selectedDate.value = `${year}-${month}-${day}`;
  emit('update:modelValue', selectedDate.value);
  showCalendar.value = false;
};

// Navigation du calendrier
const previousMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + 1,
    1
  );
};

// Raccourcis de date
const setToday = () => {
  selectDate(new Date());
};

const setTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  selectDate(tomorrow);
};

const setNextWeek = () => {
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  selectDate(nextWeek);
};

const clearDate = () => {
  selectedDate.value = '';
  emit('update:modelValue', '');
  showCalendar.value = false;
};

const closeCalendar = () => {
  showCalendar.value = false;
};
</script>

<template>
  <div class="relative">
    <label
      v-if="label"
      class="block text-sm font-medium text-[#1D1D1F] dark:text-white mb-1.5"
    >
      {{ label }}
    </label>

    <button
      type="button"
      class="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border-2 border-[#E5E7EB] dark:border-[#2A2A2C] bg-white dark:bg-[#1D1D1F] text-[#1D1D1F] dark:text-white hover:border-[#3860F9]/40 dark:hover:border-[#D3FD58]/40 focus:outline-none focus:border-[#3860F9] dark:focus:border-[#D3FD58] transition-colors"
      :class="{ 'text-[#6B7280] dark:text-[#9CA3AF]': !selectedDate }"
      @click="showCalendar = !showCalendar"
    >
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-calendar size-4 text-[#6B7280] dark:text-[#9CA3AF]"
        />
        <span>{{ formattedDate || placeholder }}</span>
      </div>
      <span
        v-if="selectedDate"
        class="i-lucide-x size-4 text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#1D1D1F] dark:hover:text-white"
        @click.stop="clearDate"
      />
    </button>

    <!-- Calendrier déroulant -->
    <div
      v-if="showCalendar"
      v-on-click-outside="closeCalendar"
      class="absolute z-50 mt-2 p-4 bg-white dark:bg-[#1D1D1F] border-2 border-[#E5E7EB] dark:border-[#2A2A2C] rounded-xl shadow-2xl w-80"
    >
      <!-- Raccourcis -->
      <div
        class="flex items-center gap-2 mb-4 pb-3 border-b border-[#E5E7EB] dark:border-[#2A2A2C]"
      >
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors"
          @click="setToday"
        >
          {{ $t('TASKS.DATE_PICKER.TODAY') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors"
          @click="setTomorrow"
        >
          {{ $t('TASKS.DATE_PICKER.TOMORROW') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors"
          @click="setNextWeek"
        >
          {{ $t('TASKS.DATE_PICKER.IN_7_DAYS') }}
        </button>
      </div>

      <!-- Navigation du mois -->
      <div class="flex items-center justify-between mb-4">
        <button
          type="button"
          class="p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors"
          @click="previousMonth"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <span
          class="text-sm font-semibold text-[#1D1D1F] dark:text-white capitalize"
        >
          {{ monthYearLabel }}
        </span>
        <button
          type="button"
          class="p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors"
          @click="nextMonth"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>

      <!-- Jours de la semaine -->
      <div class="grid grid-cols-7 gap-1 mb-2">
        <div
          v-for="day in weekDays"
          :key="day"
          class="text-center text-[10px] font-semibold text-[#6B7280] dark:text-[#9CA3AF] py-1"
        >
          {{ day }}
        </div>
      </div>

      <!-- Grille de jours -->
      <div class="grid grid-cols-7 gap-1">
        <button
          v-for="(day, index) in calendarDays"
          :key="index"
          type="button"
          class="aspect-square flex items-center justify-center text-xs rounded-lg transition-all"
          :class="{
            'text-[#6B7280] dark:text-[#9CA3AF] cursor-default':
              !day.isCurrentMonth,
            'hover:bg-[#F9FAFB] dark:hover:bg-[#1D1D1F] text-[#1D1D1F] dark:text-white cursor-pointer':
              day.isCurrentMonth,
            'bg-[#3860F9] dark:bg-[#D3FD58] text-white dark:text-[#1D1D1F] hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] font-semibold':
              isSelected(day.fullDate),
            'ring-2 ring-[#3860F9]/30 dark:ring-[#D3FD58]/30':
              isToday(day.fullDate) && !isSelected(day.fullDate),
          }"
          :disabled="!day.isCurrentMonth"
          @click="selectDate(day.fullDate)"
        >
          {{ day.date }}
        </button>
      </div>
    </div>
  </div>
</template>
